import { css, cx } from "styled-system/css";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Users, CalendarDays, TrendingUp, ArrowUpRight, Activity, Clock, CalendarCheck } from "lucide-react";
import { AdminCharts } from "@/components/admin/admin-charts";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";

type Data = {
  kpis: {
    activePros: number;
    turnsDay: number;
    turnsWeek: number;
    turnsMonth: number;
    freeCount: number;
    paidCount: number;
    conversion: number;
    totalAppts: number;
    totalPros: number;
    mrrReal: number;
    mrrPotential: number;
  };
  charts: {
    userGrowthLabels: string[];
    userGrowthData: number[];
    categoryLabels: string[];
    categoryData: number[];
    revenueLabels: string[];
    revenueData: number[];
    planLabels: string[];
    planData: number[];
    topProLabels: string[];
    topProData: number[];
    activityLabels: string[];
    activityData: number[];
  };
};

function formatARS(n: number) {
  return `$${n.toLocaleString("es-AR")}`;
}

function KpiCard({
  icon,
  label,
  value,
  sub,
  accentBg,
  accentText,
  topColor,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  sub?: string;
  accentBg: string;
  accentText: string;
  topColor: string;
}) {
  return (
    <div
      className={cx(cf.dashSurface, css({ p: "5", borderTopWidth: "3px", display: "flex", flexDir: "column", gap: "0" }))}
      style={{ borderTopColor: topColor }}
    >
      <div className={css({ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "3" })}>
        <span
          className={css({
            fontSize: "xs",
            fontWeight: "medium",
            color: "#64748b",
            lineHeight: "1.3",
            maxW: "10rem",
          })}
        >
          {label}
        </span>
        <div
          className={css({ w: "8", h: "8", rounded: "lg", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, ml: "2" })}
          style={{ backgroundColor: accentBg, color: accentText }}
        >
          {icon}
        </div>
      </div>
      <p className={css({ fontSize: "2xl", fontWeight: "bold", color: "#0f172a", letterSpacing: "tight", lineHeight: "1" })}>
        {value}
      </p>
      {sub && (
        <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "1.5", lineHeight: "1.4" })}>{sub}</p>
      )}
    </div>
  );
}

function SecondaryMetric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className={css({ display: "flex", flexDir: "column", gap: "1", py: "1" })}>
      <span className={css({ fontSize: "xs", color: "#94a3b8", fontWeight: "medium" })}>{label}</span>
      <span className={css({ fontSize: "lg", fontWeight: "bold", color: "#0f172a", letterSpacing: "tight" })}>{value}</span>
    </div>
  );
}

export function AdminHome() {
  const { refresh } = useAuth();
  const [d, setD] = useState<Data | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await fetchWithSessionSync("/api/admin/metrics", { credentials: "include" }, refresh);
      if (cancelled) return;
      if (!r.ok) {
        if (r.status !== 401 && r.status !== 403) setLoadError(true);
        return;
      }
      setD((await r.json()) as Data);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  if (loadError) {
    return (
      <AdminPageShellInline title="Métricas" description="No se pudieron cargar las métricas. Probá recargar la página.">
        <p className={css({ fontSize: "sm", color: "#64748b" })}>Si el problema sigue, comprobá la conexión con el servidor.</p>
      </AdminPageShellInline>
    );
  }

  if (!d) {
    return (
      <AdminPageShellInline title="Métricas">
        <LoadingFrame variant="light" minHeight={420} />
      </AdminPageShellInline>
    );
  }

  const { kpis, charts } = d;
  const mrrReal = kpis.mrrReal ?? 0;
  const mrrPotential = kpis.mrrPotential ?? 0;

  return (
    <div>
      {/* Page header */}
      <div className={css({ mb: "7" })}>
        <p className={css({ fontSize: "xs", color: "#64748b", letterSpacing: "widest", mb: "2", textTransform: "uppercase" })}>
          Panel Global
        </p>
        <h1 className={css({ fontSize: { base: "2xl", md: "3xl" }, fontWeight: "bold", letterSpacing: "tight", color: "#0f172a" })}>
          Métricas de plataforma
        </h1>
        <p className={css({ mt: "1.5", fontSize: "sm", color: "#64748b" })}>
          Vista unificada de crecimiento, actividad, planes e ingresos.
        </p>
      </div>

      {/* KPI cards — 4 columnas */}
      <div
        className={css({
          display: "grid",
          gridTemplateColumns: { base: "1fr", sm: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" },
          gap: "4",
          mb: "6",
        })}
      >
        <KpiCard
          icon={<Users size={16} />}
          label="Cuentas activas"
          value={String(kpis.activePros)}
          sub={`${kpis.freeCount} Free · ${kpis.paidCount} de pago`}
          accentBg="#eef2ff"
          accentText="#4f46e5"
          topColor="#6366f1"
        />
        <KpiCard
          icon={<CalendarCheck size={16} />}
          label="Turnos este mes"
          value={String(kpis.turnsMonth)}
          sub={`Hoy: ${kpis.turnsDay} · Semana: ${kpis.turnsWeek}`}
          accentBg="#f0fdf4"
          accentText="#16a34a"
          topColor="#22c55e"
        />
        <KpiCard
          icon={<TrendingUp size={16} />}
          label="MRR Real"
          value={formatARS(mrrReal)}
          sub={`Potencial: ${formatARS(mrrPotential)}`}
          accentBg="#fffbeb"
          accentText="#d97706"
          topColor="#f59e0b"
        />
        <KpiCard
          icon={<ArrowUpRight size={16} />}
          label="Conversión Free a pago"
          value={`${(kpis.conversion * 100).toFixed(1)}%`}
          sub={`${kpis.paidCount} de pago de ${kpis.totalPros} cuentas`}
          accentBg="#f8fafc"
          accentText="#475569"
          topColor="#94a3b8"
        />
      </div>

      {/* MRR section */}
      <div className={cx(cf.dashSurface, css({ p: "5", mb: "6" }))}>
        <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "4" })}>
          <div>
            <h2 className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>
              Ingresos mensuales recurrentes (MRR)
            </h2>
            <p className={css({ fontSize: "xs", color: "#64748b", mt: "0.5" })}>
              Real: cuentas con pago confirmado. Potencial: suma si los trials vencidos convirtieran.
            </p>
          </div>
          <Link
            to="/admin/planes"
            className={css({ fontSize: "xs", fontWeight: "medium", color: "#4f46e5", _hover: { textDecoration: "underline" }, flexShrink: 0, ml: "4" })}
          >
            Ver cuentas
          </Link>
        </div>
        <div
          className={css({
            display: "grid",
            gridTemplateColumns: { base: "1fr", sm: "1fr 1fr" },
            gap: "3",
          })}
        >
          <div
            className={css({ p: "4", rounded: "lg", borderWidth: "1px" })}
            style={{ background: "#f0fdf4", borderColor: "#bbf7d0" }}
          >
            <p className={css({ fontSize: "xs", fontWeight: "semibold", mb: "1" })} style={{ color: "#166534" }}>
              MRR Real
            </p>
            <p className={css({ fontSize: "2xl", fontWeight: "bold", letterSpacing: "tight" })} style={{ color: "#14532d" }}>
              {formatARS(mrrReal)}
            </p>
            <p className={css({ fontSize: "xs", mt: "1" })} style={{ color: "#16a34a" }}>
              Solo cuentas pagando, sin pruebas
            </p>
          </div>
          <div
            className={css({ p: "4", rounded: "lg", borderWidth: "1px" })}
            style={{ background: "#fffbeb", borderColor: "#fde68a" }}
          >
            <p className={css({ fontSize: "xs", fontWeight: "semibold", mb: "1" })} style={{ color: "#92400e" }}>
              MRR Potencial
            </p>
            <p className={css({ fontSize: "2xl", fontWeight: "bold", letterSpacing: "tight" })} style={{ color: "#78350f" }}>
              {formatARS(mrrPotential)}
            </p>
            <p className={css({ fontSize: "xs", mt: "1" })} style={{ color: "#d97706" }}>
              Real + trials vencidos sin convertir
            </p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <AdminCharts {...charts} />

      {/* Secondary metrics row */}
      <div className={cx(cf.dashSurface, css({ p: "5", mt: "-2" }))}>
        <h2 className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", mb: "4" })}>
          Métricas adicionales
        </h2>
        <div
          className={css({
            display: "grid",
            gridTemplateColumns: { base: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(5, 1fr)" },
            gap: "4",
          })}
        >
          <div className={css({ display: "flex", alignItems: "center", gap: "2.5" })}>
            <div className={css({ w: "8", h: "8", rounded: "lg", bg: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 })}>
              <Clock size={14} style={{ color: "#4f46e5" }} />
            </div>
            <SecondaryMetric label="Turnos hoy" value={kpis.turnsDay} />
          </div>
          <div className={css({ display: "flex", alignItems: "center", gap: "2.5" })}>
            <div className={css({ w: "8", h: "8", rounded: "lg", bg: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 })}>
              <CalendarDays size={14} style={{ color: "#4f46e5" }} />
            </div>
            <SecondaryMetric label="Turnos semana" value={kpis.turnsWeek} />
          </div>
          <div className={css({ display: "flex", alignItems: "center", gap: "2.5" })}>
            <div className={css({ w: "8", h: "8", rounded: "lg", bg: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 })}>
              <Activity size={14} style={{ color: "#4f46e5" }} />
            </div>
            <SecondaryMetric label="Total turnos" value={kpis.totalAppts.toLocaleString("es-AR")} />
          </div>
          <div className={css({ display: "flex", alignItems: "center", gap: "2.5" })}>
            <div className={css({ w: "8", h: "8", rounded: "lg", bg: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 })}>
              <Users size={14} style={{ color: "#64748b" }} />
            </div>
            <SecondaryMetric label="Cuentas Free" value={kpis.freeCount} />
          </div>
          <div className={css({ display: "flex", alignItems: "center", gap: "2.5" })}>
            <div className={css({ w: "8", h: "8", rounded: "lg", bg: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 })}>
              <TrendingUp size={14} style={{ color: "#64748b" }} />
            </div>
            <SecondaryMetric label="Total cuentas" value={kpis.totalPros} />
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminPageShellInline({ title, description, children }: { title: string; description?: string; children?: ReactNode }) {
  return (
    <div className={css({ display: "flex", flexDir: "column", gap: "6" })}>
      <div>
        <p className={css({ fontSize: "xs", color: "#64748b", letterSpacing: "widest", mb: "2", textTransform: "uppercase" })}>Panel Global</p>
        <h1 className={css({ fontSize: "2xl", fontWeight: "bold", letterSpacing: "tight", color: "#0f172a" })}>{title}</h1>
        {description && <p className={css({ fontSize: "sm", color: "#64748b", mt: "2" })}>{description}</p>}
      </div>
      {children}
    </div>
  );
}
